/**
 * Chemins de l'application, en un seul endroit.
 *
 * Volontairement `as const` : chaque valeur est un **littéral**, pas un
 * `string`. Une faute de frappe dans un `<Link to={…}>` ou un `<Navigate>` est
 * donc rattrapée par `tsc`, et renommer une route se fait ici.
 *
 * ⚠️ Ce n'est pas un routeur typé pour autant : React Router en mode SPA ne
 * vérifie pas les paramètres d'une route dynamique. Chacune s'écrit donc en
 * **fonction** qui prend son paramètre, jamais en gabarit assemblé à l'appel —
 * c'est ce qui empêche d'oublier l'argument et de produire un `/events/` qui
 * tombe sur la 404.
 */
export const ROUTES = {
  /** Écran de connexion — le seul chemin atteignable sans session. */
  signIn: "/sign-in",
  /** Page d'arrivée, sous session. */
  dashboard: "/",
  reports: "/reports",
  /**
   * Le fil, en entier : les quatre statuts, publications retirées par leur
   * auteur comprises. La file de validation n'en est qu'un filtre
   * (`?status=pending_review`).
   */
  posts: "/posts",
  /**
   * La file de validation : le même écran, filtré.
   *
   * ⚠️ **Un chemin à part, et non un `?status=` assemblé à l'appel.** Depuis que
   * `/posts` ouvre sur tout le fil, deux écrans du tableau de bord visent la
   * file — le panneau et l'indice de la tuile —, et un filtre recopié aux deux
   * endroits finit par diverger de celui que l'écran sait relire.
   */
  postsQueue: "/posts?status=pending_review",
  /**
   * Fiche d'une publication.
   *
   * ⚠️ Le paramètre est un **id de publication** au sens de
   * `GET /moderation/post/{id}` — le même que celui porté par un signalement de
   * type `post`, ce qui rend la fiche atteignable depuis la file de modération
   * sans conversion.
   *
   * Une fonction, et non un gabarit assemblé à l'appel : c'est ce qui empêche de
   * produire un `/posts/` qui tombe sur la 404.
   */
  post: (postId: number) => `/posts/${postId}`,
  /** Le même chemin, côté routeur. */
  postPattern: "/posts/:postId",
  events: "/events",
  /**
   * Fiche d'un événement.
   *
   * ⚠️ Le paramètre est un **id d'événement** au sens de `GET /event/{id}` —
   * le même que celui porté par un signalement de type `event`, ce qui rend la
   * fiche atteignable depuis la file de modération sans conversion.
   */
  event: (eventId: number) => `/events/${eventId}`,
  /** Le même chemin, côté routeur. */
  eventPattern: "/events/:eventId",
  accounts: "/accounts",
  /**
   * Fiche d'un compte.
   *
   * ⚠️ Le paramètre est un **id de compte**, et c'est ce qui a changé avec le
   * lot A : `GET /moderation/accounts/{id}` s'indexe sur le compte, là où
   * l'ancienne fiche passait par un id de **profil**. Toute mention de compte
   * de l'admin — un signaleur, un abonné, une réaction — mène donc désormais
   * ici sans conversion.
   *
   * Une fonction, et non un gabarit assemblé à l'appel : c'est ce qui empêche
   * de produire un `/accounts/` qui tombe sur la 404.
   */
  account: (accountId: number) => `/accounts/${accountId}`,
  /** Le même chemin, côté routeur. */
  accountPattern: "/accounts/:accountId",
  settings: "/settings",
} as const;

/**
 * Un chemin de l'application.
 *
 * `Extract<…, string>` écarte les **fabriques** de chemin (`account`) : un
 * `NavItem` ou un `<Navigate>` attend une adresse, pas une fonction qui en
 * produit une.
 */
export type Route = Extract<(typeof ROUTES)[keyof typeof ROUTES], string>;
